import { environment } from "@app/relay/relay-environment";
import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import type { UppyFile } from "@uppy/core";
import { DragDrop } from "@uppy/react";
import { useEffect, useMemo, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type { statementUploader_PresignQuery } from "@app/queries/statementUploader_PresignQuery.graphql";
import type { statementUploader_UploadMutation } from "@app/queries/statementUploader_UploadMutation.graphql";
import type { UploadedFile } from "./types";

import "@uppy/core/dist/style.min.css";
import "@uppy/drag-drop/dist/style.min.css";

const presignQuery = graphql`
  query statementUploader_PresignQuery($fileExtension: String!) {
    uploadPresign(fileExtension: $fileExtension) {
      signedUrl
      storageKey
    }
  }
`;

const uploadMutation = graphql`
  mutation statementUploader_UploadMutation(
    $token: String
    $input: OnboardingStatementInputObject!
  ) {
    leadStatementUpload(token: $token, onboardingStatementInput: $input) {
      lead {
        completenessScore
      }
      errors {
        message
      }
    }
  }
`;

// CSV drop zone for one piece of software. Files go straight to S3 through a
// presigned PUT, then the mutation attaches the cached upload to the lead's row
// and parses it — parse-only, for what the hub shows back.
export default function StatementUploader({
  sourceId,
  token,
  label,
  files,
  onUploaded,
  onDelete,
}: {
  sourceId: string;
  token: string | null;
  label: string;
  files: readonly UploadedFile[];
  onUploaded: () => Promise<void>;
  onDelete: (fileId: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const uppy = useMemo(
    () =>
      new Uppy({
        restrictions: { allowedFileTypes: [".csv"], maxNumberOfFiles: 12 },
        autoProceed: true,
      }).use(AwsS3, {
        shouldUseMultipart: false,
        async getUploadParameters(file: UppyFile) {
          const extension = file.name.split(".").pop() ?? "csv";
          const data = await fetchQuery<statementUploader_PresignQuery>(
            environment,
            presignQuery,
            {
              fileExtension: extension,
            }
          ).toPromise();
          const presign = data?.uploadPresign;
          if (!presign) throw new Error("Could not presign upload");
          uppy.setFileMeta(file.id, { shrineId: presign.storageKey });
          return {
            method: "PUT",
            url: presign.signedUrl,
            fields: {},
            headers: { "Content-Type": file.type ?? "text/csv" },
          };
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const onSuccess = (file: UppyFile | undefined) => {
      if (!file) return;
      setBusy(true);
      const descriptor = {
        id: String(file.meta.shrineId),
        storage: "cache" as const,
        metadata: {
          size: file.size,
          filename: file.name,
          mimeType: file.type ?? "text/csv",
        },
      };
      commitMutation<statementUploader_UploadMutation>(environment, {
        mutation: uploadMutation,
        variables: { token, input: { uploads: [descriptor], sourceId } },
        onCompleted: async () => {
          await onUploaded();
          setBusy(false);
          uppy.cancelAll();
        },
        onError: () => setBusy(false),
      });
    };
    uppy.on("upload-success", onSuccess);
    return () => {
      uppy.off("upload-success", onSuccess);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppy]);

  useEffect(() => () => uppy.close(), [uppy]);

  return (
    <div>
      {files.length > 0 && (
        <ul>
          {files.map((file) => (
            <li key={file.id}>
              {file.filename ?? "statement.csv"}
              {file.parseState === "PARSED" &&
                ` — ${file.txnCount} transactions${
                  file.coverageStart && file.coverageEnd
                    ? `, ${file.coverageStart} to ${file.coverageEnd}`
                    : ""
                }`}
              {file.parseState === "FAILED" &&
                ` — ${file.errorMessage ?? "couldn't read this file"}`}{" "}
              <button type="button" onClick={() => onDelete(file.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Uppy's stylesheet is loaded (three other pages import it), but the
          `all: revert` inside `.onboarding-plain` strips it back off here, so
          this renders as the plain file input underneath. That is deliberate:
          how uploading should feel is one of the open questions, and a
          half-styled widget would prejudge it. */}
      <DragDrop
        uppy={uppy}
        note={busy ? "Reading it now…" : `${label} CSV export`}
      />
    </div>
  );
}
